/**
 * AcompteEditor — Card "Échéancier de paiement".
 *
 * Phase 3i Lot B — Modèles d'échéanciers :
 *   • Sélecteur de modèle (`payment_schedule_template_id`) au-dessus du toggle "Personnaliser".
 *   • Le modèle sélectionné pilote l'affichage read-only quand override désactivé.
 *   • "Personnaliser" pré-remplit `acompte_config_override` depuis le modèle sélectionné (fallback company).
 *
 * Priorité de résolution effective (côté rendu et côté serveur) :
 *   acompte_config_override > template.schedule > company.acompte_config
 */
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import BENumericInput from "@/components/quote/BENumericInput";

export default function AcompteEditor({
  quote,
  setQuote,
  isDraft,
  companySettings,
  paymentSchedules = [],
}) {
  const isOverridden =
    quote.acompte_config_override && quote.acompte_config_override.length > 0;
  const templateId = quote.payment_schedule_template_id || "";
  const selectedTemplate = paymentSchedules.find((t) => t.id === templateId);
  const fallback = companySettings?.acompte_config || [];
  const templateSchedule = selectedTemplate?.schedule || fallback;
  const effectiveAcompte = isOverridden ? quote.acompte_config_override : templateSchedule;
  const acompteTotal = effectiveAcompte.reduce((a, s) => a + Number(s.percent || 0), 0);

  const onTemplateChange = (id) => {
    setQuote({ ...quote, payment_schedule_template_id: id });
  };

  const toggleOverride = (checked) => {
    if (checked) {
      // Pré-remplit l'override depuis le modèle sélectionné (fallback company)
      setQuote({
        ...quote,
        acompte_config_override: templateSchedule.map((s) => ({ ...s })),
      });
    } else {
      setQuote({ ...quote, acompte_config_override: null });
    }
  };
  const updateStep = (i, patch) => {
    const cfg = [...(quote.acompte_config_override || [])];
    cfg[i] = { ...cfg[i], ...patch };
    setQuote({ ...quote, acompte_config_override: cfg });
  };
  const addStep = () => {
    if ((quote.acompte_config_override || []).length >= 5) return;
    setQuote({
      ...quote,
      acompte_config_override: [
        ...(quote.acompte_config_override || []),
        { label: "Étape", percent: 0 },
      ],
    });
  };
  const removeStep = (i) =>
    setQuote({
      ...quote,
      acompte_config_override: (quote.acompte_config_override || []).filter((_, k) => k !== i),
    });

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="quote-acompte-block">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wider text-slate-500">Échéancier de paiement</h3>
          <div className="flex items-center gap-2">
            <Switch
              id="tg-override"
              checked={isOverridden}
              onCheckedChange={toggleOverride}
              disabled={!isDraft}
              data-testid="toggle-acompte-override"
            />
            <Label htmlFor="tg-override" className="text-[11px] text-slate-400">
              Personnaliser
            </Label>
          </div>
        </div>

        {/* Sélecteur de modèle — désactivé si override actif (l'override prend le pas) */}
        {paymentSchedules.length > 0 && (
          <div className="space-y-1">
            <Label className="text-[11px] text-slate-400">Modèle</Label>
            <Select
              value={templateId}
              onValueChange={onTemplateChange}
              disabled={!isDraft || isOverridden}
            >
              <SelectTrigger
                className="h-8 border-slate-700 bg-slate-950/40 text-xs text-slate-100"
                data-testid="acompte-template-select"
              >
                <SelectValue placeholder="Choisir un modèle…" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                {paymentSchedules.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`acompte-template-option-${t.id}`}>
                    {t.name}
                    {t.is_default ? " · défaut" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOverridden && (
              <p className="text-[10px] text-slate-500">
                Personnalisation active — le modèle n&apos;est pas appliqué tant qu&apos;elle est activée.
              </p>
            )}
          </div>
        )}

        {!isOverridden ? (
          <div className="space-y-1 text-xs text-slate-300">
            <div className="mb-1 text-[10px] text-slate-500">
              {selectedTemplate
                ? `Modèle « ${selectedTemplate.name} »`
                : "Utilise la config par défaut de l'entreprise"}
            </div>
            {effectiveAcompte.length === 0 ? (
              <div className="text-slate-500">Aucun échéancier configuré.</div>
            ) : (
              effectiveAcompte.map((s, i) => (
                <div key={i} className="flex items-center justify-between" data-testid={`acompte-preview-${i}`}>
                  <span>{s.label}</span>
                  <span className="tabular font-semibold text-slate-200">{s.percent}%</span>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="acompte-override-editor">
            {(quote.acompte_config_override || []).map((step, i) => (
              <div key={i} className="flex items-center gap-1">
                <Input
                  value={step.label}
                  onChange={(e) => updateStep(i, { label: e.target.value })}
                  disabled={!isDraft}
                  className="h-8 border-slate-700 bg-slate-950/40 text-xs text-slate-100"
                  placeholder="Libellé"
                  data-testid={`acompte-label-${i}`}
                />
                <BENumericInput
                  value={step.percent}
                  onChange={(n) => updateStep(i, { percent: Math.max(0, Math.min(100, Math.round(n))) })}
                  disabled={!isDraft}
                  className="tabular h-8 w-16 border-slate-700 bg-slate-950/40 text-xs text-slate-100"
                  data-testid={`acompte-percent-${i}`}
                />
                <span className="text-xs text-slate-500">%</span>
                {(quote.acompte_config_override || []).length > 1 && isDraft && (
                  <button
                    onClick={() => removeStep(i)}
                    className="rounded p-1 text-red-400 hover:bg-red-500/10"
                    data-testid={`acompte-remove-${i}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {isDraft && (quote.acompte_config_override || []).length < 5 && (
              <Button
                size="sm"
                variant="outline"
                onClick={addStep}
                className="border-slate-700 bg-slate-800/60 text-xs text-slate-200"
                data-testid="acompte-add-step"
              >
                <Plus className="mr-1 h-3 w-3" /> Étape
              </Button>
            )}
            <div
              className={`mt-1 text-[10px] ${
                acompteTotal === 100 ? "text-emerald-400" : "text-red-400"
              }`}
              data-testid="acompte-total-check"
            >
              Total : {acompteTotal}% {acompteTotal === 100 ? "✓" : "(doit être 100 avant envoi)"}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
